import React from 'react';
import * as PropTypes from 'prop-types';
import {Rect, Text, Group} from 'react-konva';
import Utility from "../../Utility";
import PlantManagementUtility from "../../components/plantManagement/PlantManagementUtility";
import RotationArrow from "./RotationArrow";

const TEXT_FONT_FAMILY = 'monospace'; // デフォルトはArialだが動的にwidthとheightが変化するため等幅フォントを使用する.
const TEXT_FONT_SIZE_DEFAULT = 16;
const TEXT_FONT_SIZE_MEDIUM = 14;
const TEXT_FONT_SIZE_SMALL = 12.5;
const TEXT_STROKE_COLOR = 'black';
const TEXT_STROKE_WIDTH = 1;
const ADD_ANGLE_VALUE = 45;
const ANGLE_MAX = 360;
const ANGLE_DEFAULT = 0;
const BLINK_INTERVAL = 250;
const ROTATION_ARROW_ICON_WIDTH = TEXT_FONT_SIZE_DEFAULT / 2 * 4 + 4;
const BASE_HEIGHT = 69 * 0.7;
const SMALL_HEIGHT = BASE_HEIGHT - BASE_HEIGHT / 3;
const BASE_WIDTH = 168 * 0.7;
const BIG_WIDTH = BASE_WIDTH + BASE_WIDTH / 3;
const SMALL_WIDTH = BASE_WIDTH - BASE_WIDTH / 3;

export class HeatExchangerInLayout extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            isBlinking: false,
            isDraggable: true,
            x: PlantManagementUtility.parseInt(this.props.heatExchanger.systemInfoPositionX),
            y: PlantManagementUtility.parseInt(this.props.heatExchanger.systemInfoPositionY),
        };

        this.blinkIntervalId = 0; // 点滅停止用のインターバルID.
        this.groupRef = React.createRef(); // 最前面表示用.

        this.getTextWidth = this.getTextWidth.bind(this);
        this.isStopBlinkInterval = this.isStopBlinkInterval.bind(this);
        this.isOutOfLayout = this.isOutOfLayout.bind(this);
        this.moveLayout = this.moveLayout.bind(this);
        this.moveToTop = this.moveToTop.bind(this);
        this.onDragEnd = this.onDragEnd.bind(this);
        this.onDragMove = this.onDragMove.bind(this);
        this.onDragStart = this.onDragStart.bind(this);
        this.onTapOrClickBody = this.onTapOrClickBody.bind(this);
        this.onTapOrClickRotationArrow = this.onTapOrClickRotationArrow.bind(this);
        this.resetBlinkIntervalId = this.resetBlinkIntervalId.bind(this);
        this.setPosition = this.setPosition.bind(this);
        this.startBlink = this.startBlink.bind(this);
        this.stopBlink = this.stopBlink.bind(this);
        this.isOverflowText = this.isOverflowText.bind(this);
    }

    isStopBlinkInterval() {
        return this.blinkIntervalId === 0;
    }

    isOutOfLayout(x, y) {
        return x <= 0 || x >= this.props.layoutWidth || y <= 0 || y >= this.props.layoutHeight;
    }

    moveLayout(targetX, targetY, destinationLayoutIndex, destinationLayout) {
        // 同じx,yでsetStateしても再描画されないので一旦指定の位置にsetStateしてから移動先のレイアウトの中央に配置する.
        // isDraggable:falseでドラッグ不可にして中断させる.
        this.setState({isDraggable: false, x: targetX, y: targetY}, () => {
            let roundX = Math.round(this.props.layoutWidth / 2); // 整数に四捨五入する.
            let roundY = Math.round(this.props.layoutHeight / 2); // 整数に四捨五入する.
            let newHeatExchanger = Utility.getDeepCopy(this.props.heatExchanger);
            newHeatExchanger.systemInfoPositionX = roundX.toString();
            newHeatExchanger.systemInfoPositionY = roundY.toString();
            newHeatExchanger.sceneDetailNumber = destinationLayout.sceneDetailNumber;
            newHeatExchanger.sceneDetailName = destinationLayout.sceneDetailName;
            this.props.onMoveLayout(newHeatExchanger, destinationLayoutIndex);
            this.setState({isDraggable: true, x: roundX, y: roundY}); // isDraggable:trueでドラッグ可能に戻す.
        });
    }

    moveToTop(){
        this.groupRef.current.moveToTop();
    }

    onDragEnd(event) {
        let backupX = this.state.x;
        let backUpY = this.state.y;
        // 同じx,yでsetStateしても再描画されないのでレイアウト外に置かれて戻す場合も一旦指定の位置にsetStateしてから戻す.
        this.setState({x: event.target.x(), y: event.target.y()}, () => {
            if (this.isOutOfLayout(event.target.x(), event.target.y())) {
                this.setState({x: backupX, y: backUpY});
            } else {
                let roundX = Math.round(event.target.x()); // 整数に四捨五入する.
                let roundY = Math.round(event.target.y()); // 整数に四捨五入する.

                let newHeatExchanger = Utility.getDeepCopy(this.props.heatExchanger);
                newHeatExchanger.systemInfoPositionX = roundX.toString();
                newHeatExchanger.systemInfoPositionY = roundY.toString();
                this.props.onDragEnd(newHeatExchanger);
            }
        });
    }

    onDragMove(event) {
        if (event.target.x() >= this.props.layoutWidth) {
            // 右隣のレイアウトに移動.
            let rightIndex = this.props.currentLayoutIndex + 1;
            if (rightIndex < this.props.layoutArray.length) {
                let rightLayout = this.props.layoutArray[rightIndex];
                this.moveLayout(event.target.x(), event.target.y(), rightIndex, rightLayout);
            }
        } else if (event.target.x() <= 0) {
            // 左隣のレイアウトに移動.
            let leftIndex = this.props.currentLayoutIndex - 1;
            if (leftIndex >= 0) {
                let leftLayout = this.props.layoutArray[leftIndex];
                this.moveLayout(event.target.x(), event.target.y(), leftIndex, leftLayout);
            }
        }
    }

    onDragStart(event) {
        event.target.moveToTop(); // ドラッグをトリガーに最前面に描画する.
    }

    onTapOrClickBody() {
        this.props.onTapOrClickBody(this.props.heatExchanger);
    }

    onTapOrClickRotationArrow() {
        let rotation = PlantManagementUtility.parseInt(this.props.heatExchanger.systemInfoRotation) + ADD_ANGLE_VALUE;
        if (rotation >= ANGLE_MAX) {
            rotation = ANGLE_DEFAULT;
        }

        let newHeatExchanger = Utility.getDeepCopy(this.props.heatExchanger);
        newHeatExchanger.systemInfoRotation = rotation.toString();
        this.props.onTapOrClickRotationArrow(newHeatExchanger);
    }

    resetBlinkIntervalId() {
        this.blinkIntervalId = 0;
    }

    setPosition(x, y) {
        this.setState({
            x: PlantManagementUtility.parseInt(x),
            y: PlantManagementUtility.parseInt(y)
        });
    }

    startBlink() {
        this.blinkIntervalId = setInterval(() => {
            // ON/OFFを交互にセットする.
            this.setState({isBlinking: !this.state.isBlinking})
        }, BLINK_INTERVAL);
    }

    stopBlink() {
        if (this.isStopBlinkInterval()) {
            return;
        }

        this.setState({isBlinking: false}, () => {
            clearInterval(this.blinkIntervalId);
            this.resetBlinkIntervalId();
        });
    }

    getTextWidth() {
        let text = this.props.heatExchanger.instrumentApparatus + this.props.heatExchanger.instrumentMachineNumber;
        let textWidth = text.length * TEXT_FONT_SIZE_DEFAULT;
        const rectWidth = PlantManagementUtility.getHeatExchangerRectWidth(this.props.heatExchanger.instrumentLength);

        return this.isOverflowText() ? rectWidth : textWidth; // 矩形で折り返す分を考慮して,矩形の横幅より大きければ矩形の横幅を返す.
    }

    isOverflowText() {
        let text = this.props.heatExchanger.instrumentApparatus + this.props.heatExchanger.instrumentMachineNumber;
        let textWidthHalf = text.length * TEXT_FONT_SIZE_DEFAULT / 2;
        const rectWidthHalf = PlantManagementUtility.getHeatExchangerRectWidth(this.props.heatExchanger.instrumentLength) / 2;
        return textWidthHalf > (rectWidthHalf - ROTATION_ARROW_ICON_WIDTH); // 矢印アイコンに装置機番の表示が被るらないように考慮する.
    }

    // 熱交名が枠を超えているか確認
    isTextWidthOverflowNode() {
        let text = this.props.heatExchanger.instrumentApparatus + this.props.heatExchanger.instrumentMachineNumber;
        let textWidth = text.length * TEXT_FONT_SIZE_DEFAULT;
        const rectWidth = PlantManagementUtility.getHeatExchangerRectWidth(this.props.heatExchanger.instrumentLength);
        if (rectWidth === BASE_WIDTH) {
            return textWidth - 27 > rectWidth;
        } else if (rectWidth === BIG_WIDTH) {
            return textWidth - 52 > rectWidth;
        }
        return textWidth - 2 > rectWidth;
    }

    render() {
        let heatExchangerState = PlantManagementUtility.getHeatExchangerState(this.props.heatExchanger);
        let text = this.props.heatExchanger.instrumentApparatus + this.props.heatExchanger.instrumentMachineNumber;
        const width = PlantManagementUtility.getHeatExchangerRectWidth(this.props.heatExchanger.instrumentLength);
        const height = PlantManagementUtility.getHeatExchangerRectHeight(this.props.heatExchanger.instrumentNumberOfLines);
        const rotationAllowMargin = this.isOverflowText() ? ROTATION_ARROW_ICON_WIDTH : 0;
        let fontSize = TEXT_FONT_SIZE_DEFAULT
        let textY_pos = -TEXT_FONT_SIZE_DEFAULT / 2
        if(this.isTextWidthOverflowNode()) {
            text = this.props.heatExchanger.instrumentApparatus + '\n'+this.props.heatExchanger.instrumentMachineNumber;
            textY_pos = textY_pos - 4
            if(width === SMALL_WIDTH || height === SMALL_HEIGHT) {
                fontSize = TEXT_FONT_SIZE_SMALL 
            }else  {
                textY_pos = textY_pos - 10
                fontSize = TEXT_FONT_SIZE_MEDIUM
            }
        }
        
        return (
            <Group
                ref={this.groupRef}
                visible={this.props.visible}
                draggable={this.props.isDisabled ? false : this.state.isDraggable }
                onDragStart={this.props.isDisabled ? () => {} :this.onDragStart}
                onDragEnd={this.props.isDisabled ? () => {} :this.onDragEnd}
                onDragMove={this.props.isDisabled ? () => {} :this.onDragMove}
                x={this.state.x}
                y={this.state.y}
                rotation={PlantManagementUtility.parseInt(this.props.heatExchanger.systemInfoRotation)}
                opacity={this.state.isBlinking ? 0 : 1}
            >
                <Rect
                    onTap={this.props.isDisabled ? () => {} : this.onTapOrClickBody}
                    onClick={this.props.isDisabled ? () => {} : this.onTapOrClickBody}
                    width={width}
                    height={height}
                    fill={PlantManagementUtility.getBackgroundColorByHeatExchangerState(heatExchangerState)}
                    stroke={TEXT_STROKE_COLOR}
                    strokeWidth={TEXT_STROKE_WIDTH}
                    x={-width / 2}
                    y={-height / 2}
                />
                <Text
                    wrap={"none"}
                    x={-this.getTextWidth() / 2 + rotationAllowMargin}
                    y={textY_pos}
                    text={text}
                    width={this.getTextWidth() - rotationAllowMargin}
                    fill={PlantManagementUtility.getTextColorByHeatExchangerState(heatExchangerState)}
                    fontSize={fontSize}
                    fontFamily={TEXT_FONT_FAMILY}
                    onTap={this.props.isDisabled ? () => {} : this.onTapOrClickBody}
                    onClick={this.props.isDisabled ? () => {} : this.onTapOrClickBody}
                />
                <RotationArrow
                    onTapOrClick={this.props.isDisabled ? () => {} : this.onTapOrClickRotationArrow}
                    scale={0.8}
                    x={-width / 2}
                    y={-height / 2}
                />
            </Group>
        );
    }
}

HeatExchangerInLayout.propTypes = {
    currentLayoutIndex: PropTypes.number.isRequired,
    heatExchanger: PropTypes.object.isRequired,
    layoutArray: PropTypes.array.isRequired,
    layoutHeight: PropTypes.number.isRequired,
    layoutWidth: PropTypes.number.isRequired,
    onDragEnd: PropTypes.func.isRequired,
    onMoveLayout: PropTypes.func.isRequired,
    onTapOrClickBody: PropTypes.func.isRequired,
    onTapOrClickRotationArrow: PropTypes.func.isRequired,
    visible: PropTypes.bool.isRequired,
    isDisabled: PropTypes.bool.isRequired,
};

export default HeatExchangerInLayout;
